import { MetaManager } from "@/components/seo/MetaManager";
import { MainNavigation } from "@/components/layout/MainNavigation";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MapPin, Users, Building2 } from "lucide-react";

const US_STATES = [
  { name: 'Alabama', abbr: 'AL', url: 'alabama' },
  { name: 'Alaska', abbr: 'AK', url: 'alaska' },
  { name: 'Arizona', abbr: 'AZ', url: 'arizona' },
  { name: 'Arkansas', abbr: 'AR', url: 'arkansas' },
  { name: 'California', abbr: 'CA', url: 'california' },
  { name: 'Colorado', abbr: 'CO', url: 'colorado' },
  { name: 'Connecticut', abbr: 'CT', url: 'connecticut' },
  { name: 'Delaware', abbr: 'DE', url: 'delaware' },
  { name: 'Florida', abbr: 'FL', url: 'florida' },
  { name: 'Georgia', abbr: 'GA', url: 'georgia' },
  { name: 'Hawaii', abbr: 'HI', url: 'hawaii' },
  { name: 'Idaho', abbr: 'ID', url: 'idaho' },
  { name: 'Illinois', abbr: 'IL', url: 'illinois' },
  { name: 'Indiana', abbr: 'IN', url: 'indiana' },
  { name: 'Iowa', abbr: 'IA', url: 'iowa' },
  { name: 'Kansas', abbr: 'KS', url: 'kansas' },
  { name: 'Kentucky', abbr: 'KY', url: 'kentucky' },
  { name: 'Louisiana', abbr: 'LA', url: 'louisiana' },
  { name: 'Maine', abbr: 'ME', url: 'maine' },
  { name: 'Maryland', abbr: 'MD', url: 'maryland' },
  { name: 'Massachusetts', abbr: 'MA', url: 'massachusetts' },
  { name: 'Michigan', abbr: 'MI', url: 'michigan' },
  { name: 'Minnesota', abbr: 'MN', url: 'minnesota' },
  { name: 'Mississippi', abbr: 'MS', url: 'mississippi' },
  { name: 'Missouri', abbr: 'MO', url: 'missouri' },
  { name: 'Montana', abbr: 'MT', url: 'montana' },
  { name: 'Nebraska', abbr: 'NE', url: 'nebraska' },
  { name: 'Nevada', abbr: 'NV', url: 'nevada' },
  { name: 'New Hampshire', abbr: 'NH', url: 'new-hampshire' },
  { name: 'New Jersey', abbr: 'NJ', url: 'new-jersey' },
  { name: 'New Mexico', abbr: 'NM', url: 'new-mexico' },
  { name: 'New York', abbr: 'NY', url: 'new-york' },
  { name: 'North Carolina', abbr: 'NC', url: 'north-carolina' },
  { name: 'North Dakota', abbr: 'ND', url: 'north-dakota' },
  { name: 'Ohio', abbr: 'OH', url: 'ohio' },
  { name: 'Oklahoma', abbr: 'OK', url: 'oklahoma' },
  { name: 'Oregon', abbr: 'OR', url: 'oregon' },
  { name: 'Pennsylvania', abbr: 'PA', url: 'pennsylvania' },
  { name: 'Rhode Island', abbr: 'RI', url: 'rhode-island' },
  { name: 'South Carolina', abbr: 'SC', url: 'south-carolina' },
  { name: 'South Dakota', abbr: 'SD', url: 'south-dakota' },
  { name: 'Tennessee', abbr: 'TN', url: 'tennessee' },
  { name: 'Texas', abbr: 'TX', url: 'texas' },
  { name: 'Utah', abbr: 'UT', url: 'utah' },
  { name: 'Vermont', abbr: 'VT', url: 'vermont' },
  { name: 'Virginia', abbr: 'VA', url: 'virginia' },
  { name: 'Washington', abbr: 'WA', url: 'washington' },
  { name: 'West Virginia', abbr: 'WV', url: 'west-virginia' },
  { name: 'Wisconsin', abbr: 'WI', url: 'wisconsin' },
  { name: 'Wyoming', abbr: 'WY', url: 'wyoming' },
];

const REGIONS = [
  {
    name: 'Northeast',
    url: '/regions/northeast',
    states: ['CT', 'ME', 'MA', 'NH', 'NJ', 'NY', 'PA', 'RI', 'VT']
  },
  {
    name: 'South',
    url: '/regions/south',
    states: ['AL', 'AR', 'DE', 'FL', 'GA', 'KY', 'LA', 'MD', 'MS', 'NC', 'SC', 'TN', 'TX', 'VA', 'WV']
  },
  {
    name: 'West',
    url: '/regions/west',
    states: ['AK', 'AZ', 'CA', 'CO', 'HI', 'ID', 'MT', 'NV', 'NM', 'OR', 'UT', 'WA', 'WY']
  },
  {
    name: 'Midwest',
    url: '/regions/midwest',
    states: ['IL', 'IN', 'IA', 'KS', 'MI', 'MN', 'MO', 'NE', 'ND', 'OH', 'SD', 'WI']
  },
];

export const StatesDirectory = () => {
  return (
    <div className="min-h-screen bg-background">
      <MetaManager
        title="PEO Services by State: Find Professional Employer Organizations Near You"
        description="Find top PEO providers in all 50 states. State-specific guides to Professional Employer Organization services, compliance requirements, and local providers."
        keywords="PEO by state, state PEO services, Professional Employer Organization by location, local PEO providers"
        canonical="https://thepeoapp.com/states"
      />
      
      <MainNavigation />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                PEO Services by State
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Find Professional Employer Organization providers tailored to your state's specific employment laws, 
                compliance requirements, and business environment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="bg-card p-8 rounded-xl shadow-lg">
                <MapPin className="h-10 w-10 text-primary mb-4" />
                <h2 className="text-2xl font-bold mb-4">State-Specific Expertise</h2>
                <p className="text-muted-foreground">
                  Every state has unique employment laws, tax requirements, and compliance obligations. 
                  Our PEO partners specialize in local regulations to keep your business compliant.
                </p>
              </div>
              
              <div className="bg-card p-8 rounded-xl shadow-lg">
                <Building2 className="h-10 w-10 text-primary mb-4" />
                <h2 className="text-2xl font-bold mb-4">Local Business Understanding</h2>
                <p className="text-muted-foreground">
                  From Silicon Valley startups to Texas manufacturing, our PEO network understands 
                  the unique challenges and opportunities in your local market.
                </p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-8 text-center">Browse by Region</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {REGIONS.map((region) => (
                  <Link
                    key={region.name}
                    to={region.url}
                    className="bg-primary/5 p-6 rounded-xl border border-primary/20 hover:bg-primary/10 transition-colors group"
                  >
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                      {region.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {region.states.length} states
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {region.states.join(', ')}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-8 text-center">All States</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {US_STATES.map((state) => (
                  <Link
                    key={state.abbr}
                    to={`/states/${state.url}`}
                    className="bg-card p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center space-x-3 border group"
                  >
                    <span className="text-primary font-bold text-lg group-hover:scale-110 transition-transform">
                      {state.abbr}
                    </span>
                    <span className="text-foreground font-medium">
                      {state.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="text-center bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Ready to Find PEO Providers in Your State?</h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Get matched with PEO providers who understand your state's requirements and can help your business thrive.
              </p>
              <Link to="/signup">
                <Button size="lg" className="text-lg px-8 py-6">
                  Get Free PEO Quotes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};